import {useMemo} from 'react'
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {useViewerClassroomRolesQuery} from '#/state/queries/classrooms/roles'
import {useProfileQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import {findCreatorRole, findOrgType} from '#/screens/Onboarding/StepGoal/data'
import {
  canManageClassroom,
  type Classroom,
  type ClassroomActor,
  findMember,
  hasRequested,
  type MemberRole,
} from './types'

/** The account kinds onboarding records for a person. */
export type AccountType = 'learner' | 'parent' | 'creator' | 'organisation'

/**
 * The account's type, as the profile carries it.
 *
 * The appview does not return the field on `getProfile` yet, so it arrives
 * attached to the profile object by a custom-field route. Two names carry it:
 * onboarding's `putOnboardingData` writes `accountType`, and the profile badge
 * every card and the profile header carry — `profile.type` is the one the app
 * actually reads, because that is the field the badge is drawn from. Both are
 * read here, `accountType` first, so neither copy can disagree with the other
 * about who somebody is.
 *
 * Unknown is a real state, and callers have to say what they do about it
 * rather than pretending it is a learner.
 */
export function readAccountType(profile: unknown): AccountType | null {
  const p = profile as {accountType?: unknown; type?: unknown} | undefined
  const value = p?.accountType ?? p?.type
  if (
    value === 'learner' ||
    value === 'parent' ||
    value === 'creator' ||
    value === 'organisation'
  ) {
    return value
  }
  return null
}

/**
 * The learner's own stage and year, as the profile carries them.
 *
 * Onboarding writes both on the account (`buildPayload`), and they come back by
 * the custom-field route the account type does — `gradeLevel` is the label the
 * person picked in their country's own words (`1st year`), not a record code,
 * which is exactly what the classroom's matching compares against (see
 * `learnerMayJoinClassroom`). Absent is a real answer on a deployment that does
 * not hand the fields back yet, and no such account is ever blocked by it.
 */
export function readLearnerLevel(profile: unknown): {
  learningStage: string | null
  gradeLevel: string | null
} {
  const p = profile as
    {learningStage?: unknown; gradeLevel?: unknown} | undefined
  const stage =
    typeof p?.learningStage === 'string' ? p.learningStage.trim() : ''
  const grade = typeof p?.gradeLevel === 'string' ? p.gradeLevel.trim() : ''
  return {
    learningStage: stage || null,
    gradeLevel: grade || null,
  }
}

/**
 * The country the account comes from, as onboarding recorded it.
 *
 * The same custom-field route as the account type and the level, and the same
 * rule about silence: onboarding writes the country when the account is made
 * (`buildPayload`), and a deployment that does not hand it back yet, or an
 * account that never answered, leaves it unknown — and unknown refuses nobody
 * (`learnerMayJoinClassroom`). It is read here because a classroom takes the
 * learners of its own country and the room's country comes from its school.
 */
export function readLearnerCountry(profile: unknown): string | null {
  const value = (profile as {country?: unknown} | undefined)?.country
  if (typeof value !== 'string') return null
  const trimmed = value.trim().toUpperCase()
  return trimmed || null
}

/**
 * What the account that runs a classroom *is*, in the app's own words.
 *
 * A classroom's page talks about the account behind it, and until now it had one
 * word for that account — *the school* — because the rooms the app knew were
 * schools' rooms. Onboarding records four kinds of account (`AccountType`) and
 * then refines the two that may run a room: an organisation answers the org list
 * (a primary school, a library, a mosque school), and a creator answers a role
 * (a tutor, a coach, a trainer). So *the school* is one answer out of forty, and
 * a coach reading it about their own room is being told the app does not know
 * what they are — the same complaint `ownerNoun` answers for the rooms' own copy.
 *
 * The answer is the kind's entry and its label, in the words the account
 * vocabulary already uses for the same account in two other places — the badge
 * under a profile's handle (`ProfileHeaderHandle`) and the sub-line of a post's
 * byline (`PostMeta`) — and a room naming its owner differently from the way that
 * owner's own profile names them would be two answers to one question. The
 * labels are that vocabulary's own (`ORG_TYPES`, `CREATOR_ROLES`), never spelled
 * out again here.
 *
 * The icon is not: a menu row draws a line icon at one size, and the list holds
 * forty kinds of account, which is forty drawings to tell apart at twenty points.
 * So the icon says only *which of the two things this is* — an institution or a
 * person ({@link OwnerKindIcon}) — and the refinement is left to the words.
 *
 * Two silences, and they are not the same answer:
 *
 * - **The kind is known, the refinement is not**: an organisation whose `orgType`
 *   was never recorded is named *Organisation*, and a creator with no role
 *   *Creator*, each with the icon the create-account step gave that kind. That is
 *   what the byline says about the same account, and it is a true answer rather
 *   than a missing one.
 * - **The kind itself is unknown**: an account this device has not been told about
 *   answers `null`, and the caller decides what to do with *not known* rather than
 *   being handed a guess — the row that names an owner is not drawn at all
 *   (`ClassroomHeaderActions`, and `kindOfAccountType` for the moment before the
 *   profile arrives).
 *
 * A learner or a parent answers `null` too, and not out of doubt: a room is not
 * theirs to run (`canCreateClassroom` refuses them), so a room whose owner
 * reads as one has nothing honest to say about them.
 */
/**
 * The two things the app draws the account behind a room as.
 *
 * Not forty icons for forty kinds: an organisation answers a list that holds
 * schools, universities, libraries and mosques, and a creator answers a role —
 * tutor, coach — but a menu row is one line icon at one size. Two drawings are
 * enough to say *what kind of account this is*, and the row's words carry the
 * rest (`Primary School`, `Coach`, `Organisation`), which is the half a reader
 * can actually use.
 */
export type OwnerKindIcon = 'organisation' | 'person'

export type OwnerKind = {
  /** The kind's own name: `Primary School`, `Coach`, `Organisation`. */
  label: MessageDescriptor
  /** Which of the two drawings names it ({@link OwnerKindIcon}). */
  icon: OwnerKindIcon
}

/** A string field of a profile nobody has vouched for, or nothing. */
function idOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * The kind of account that runs a classroom, refined by what it said it is.
 *
 * Both halves come off the profile, through the same custom-field route the
 * account type does (`readAccountType`): `orgType` for an organisation and
 * `creatorRole` for a creator, which are the answers the goal step collected and
 * `putOnboardingData` wrote.
 */
export function readOwnerKind(profile: unknown): OwnerKind | null {
  const accountType = readAccountType(profile)
  const kind = kindOfAccountType(accountType)
  if (!kind || !accountType) return null
  const fields = profile as
    {creatorRole?: unknown; orgType?: unknown} | undefined
  if (accountType === 'organisation') {
    const entry = findOrgType(idOf(fields?.orgType))
    return entry ? {label: entry.label, icon: kind.icon} : kind
  }
  const role = findCreatorRole(idOf(fields?.creatorRole))
  return role ? {label: role.label, icon: kind.icon} : kind
}

/**
 * The same answer from the kind alone, for the moment the profile is on its way.
 *
 * A room records the kind of account that made it (`Classroom.ownerAccountType`),
 * which is enough to name the kind and not the refinement — an organisation is an
 * organisation whether it is a school or a library, and the row that opens its
 * page can say so while the profile it would have said more from is still in
 * flight. It is also the whole of the answer on a deployment that does not hand
 * the fields back yet, which is why the caller has one rather than waiting for
 * `readOwnerKind`: the row is a way to the owner's page, and that way must not
 * exist only on the deployments that happen to enrich the profile.
 *
 * Takes the room's stored string rather than an `AccountType` because that is
 * what a record older than the field, or one written by another build, has to
 * offer: anything this app cannot name answers `null`, which is also what a
 * learner and a parent — who do not run rooms — answer.
 */
export function kindOfAccountType(
  accountType: string | null | undefined,
): OwnerKind | null {
  switch (accountType) {
    case 'organisation':
      return {label: msg`Organisation`, icon: 'organisation'}
    case 'creator':
      return {label: msg`Creator`, icon: 'person'}
    default:
      return null
  }
}

export function useAccountType(): AccountType | null {
  const {currentAccount} = useSession()
  const {data: profile} = useProfileQuery({did: currentAccount?.did})
  return useMemo(() => readAccountType(profile), [profile])
}

/**
 * The kind of account that runs a classroom, whoever it is.
 *
 * The same answer `useAccountType` gives about the account in front of the app,
 * asked about the account behind a room — because the app's copy about the account
 * that runs a room has to be able to say *school* to one room and *tutor* to the
 * next (`ownerNoun`), and a school reading its own room is only the common case.
 * Read from the owner's profile, which is where onboarding wrote it, and through
 * the same custom-field route (`readAccountType`), so a deployment that does not
 * hand it back answers *unknown* rather than guessing: an unknown owner is named
 * the way every room's owner used to be.
 */
export function useOwnerAccountType(
  ownerDid: string | undefined,
): AccountType | null {
  const {data: profile} = useProfileQuery({did: ownerDid})
  return useMemo(() => readAccountType(profile), [profile])
}

/**
 * Whether this account may put work into a classroom.
 *
 * A classroom is a school's room: the school that runs it, and the teachers
 * and creators in it, are the ones who post. A student and a parent read the
 * room they were let into — and a learner never posts in a classroom, not even
 * a room it somehow owns. The account type is the first word, the seat is the
 * second.
 */
export function canPostInClassroom(
  classroom: Classroom,
  actor: ClassroomActor | undefined,
): boolean {
  if (!actor) return false
  if (actor.accountType === 'learner') return false
  if (actor.did === classroom.ownerDid) return true
  const member = findMember(classroom, actor.did)
  if (member?.role === 'teacher') return true
  return false
}

/**
 * Whether this account may change what the classroom is.
 *
 * The school's own account, and the moderators it has named. What a room *is* —
 * its name, its bio, the level it teaches — is the institution's decision, and
 * a moderator is the seat the institution gave that decision to; a teacher
 * without that seat puts work in the room and nothing else. Deleting the room is
 * the one decision that does not travel with the seat
 * (`canDeleteClassroom`).
 */
export function canEditClassroom(
  classroom: Classroom,
  actor: ClassroomActor | undefined,
): boolean {
  return canManageClassroom(classroom, actor?.did)
}

/**
 * Whether this account may hand out the way in.
 *
 * The school that runs the classroom, and the teachers in it: whoever is trusted
 * with the room's work is trusted with its code.
 */
export function canInviteToClassroom(
  classroom: Classroom,
  actor: ClassroomActor | undefined,
): boolean {
  if (!actor) return false
  if (actor.did === classroom.ownerDid) return true
  return findMember(classroom, actor.did)?.role === 'teacher'
}

/**
 * Whether the reader is in the room a post says it was written in.
 *
 * The answer is the device's own: a room is held here (`#/state/classrooms`),
 * so a reader is a member of it only on a device that has it. A post from a
 * room this device has never seen is read as the post of a room the reader is
 * not in, which is what it is.
 */
/**
 * The rows a classroom's menu offers, and to whom.
 *
 * The menu is a list of decisions before it is a list of rows, and they are read
 * here rather than inside the component because each one is a rule that would go
 * wrong quietly: who may invite, who may answer a request, whether the ask is
 * offered at all, whether the code is, and whether the app can say who runs the
 * room at all ({@link OwnerKind}). The component draws whatever this returns, in
 * the order it returns them.
 *
 * Two guesses the menu used to make are gone. The way in is the room's own — an
 * ask at the room that can be found, a code at the room that cannot — so nobody
 * is offered both, and somebody who has already asked is offered neither: the
 * store answers `already-requested`, and a menu must not offer a door that is
 * already knocked on — the pill above it says *Request pending*, which is the
 * truth of where that person is standing. And the owner's row is drawn when the
 * app can *name* them. It used to be a school's row and no one else's, drawn for
 * a school and hidden for a library or a tutor, because its words said *school*
 * whatever the account behind it was; the words and the icon are now the owner's
 * own kind (`readOwnerKind`), so every owner the app can name has a page to open
 * and the row says whose it is. What is left out is what could not be said: an
 * owner this device has not been told about is not drawn and renamed.
 */
export type ClassroomMenuItem =
  | 'invite'
  | 'settings'
  | 'requests'
  | 'ask'
  | 'code'
  | 'copyLink'
  | 'owner'
  | 'delete'
  | 'leave'

export function classroomMenuItems(
  classroom: Classroom,
  actor: ClassroomActor | undefined,
  ownerNamed: boolean,
): ClassroomMenuItem[] {
  const member = findMember(classroom, actor?.did)
  const requested = hasRequested(classroom, actor?.did)
  const isOwner = !!actor && actor.did === classroom.ownerDid
  const canManage = canEditClassroom(classroom, actor)
  const items: ClassroomMenuItem[] = []
  if (canInviteToClassroom(classroom, actor)) items.push('invite')
  if (canManage) items.push('settings')
  if (canManage || member?.role === 'teacher') items.push('requests')
  /*
   * The way in is one door or the other, never both.
   *
   * A classroom that can be found is entered by **asking** — the school still
   * decides, which is what asking is for (`requestToJoin`). A classroom nobody
   * can find is entered by its **code**, which is the half its school says out
   * loud (`joinClassroom` answers `code-required` there, and only there).
   *
   * The menu used to offer both to a public room's visitor — *Ask to join* and
   * *Join with a code* on one list, about a room that is not a secret. A code row
   * beside an ask says the room is the kind you cannot find, which is the one
   * thing a public room is not, and it makes the reader pick between two doors
   * that are really one (`createClassroom` gives every room a code, so the row did
   * work — it was simply the wrong door for this room).
   *
   * Either row is for somebody outside the room and not already waiting at its
   * door: a person who has asked is shown the request they are waiting on instead,
   * which is where the pill above puts them.
   */
  if (!member && !requested) {
    items.push(classroom.visibility === 'public' ? 'ask' : 'code')
  }
  /*
   * The link is the room's own address, whatever kind of room it is.
   *
   * It used to be a public room's to hand around and a private room's secret —
   * a link a school kept to a QR it showed in person. That is not what a link is
   * any more: it names the school and the room's id, and it opens the room's own
   * page, so a private room's link says *what this room is* and no more than
   * that. Its work still needs the code, which is the
   * half a school says out loud, so there is nothing left for the copy button to
   * give away.
   */
  items.push('copyLink')
  if (!isOwner && ownerNamed) items.push('owner')
  /* The exits are the room's own: a school closes it, a member leaves it. */
  if (isOwner) items.push('delete')
  else if (member) items.push('leave')
  return items
}

/**
 * The seat this account holds in the room a post was written in.
 *
 * A post written in a room is addressed to somebody - the room's members, or its
 * teachers, or everybody - and *who may read it* is that answer asked with the
 * seat the reader holds (`isClassroomPostVisibleTo`). The seat is the server's
 * answer (`getViewerRoles`), and `null` for a room the viewer is not in.
 */
export function useClassroomPostRole(
  mark: {id: string} | null | undefined,
): MemberRole | null {
  const {data: roles} = useViewerClassroomRolesQuery()
  return (mark && roles?.get(mark.id)) ?? null
}

export function useClassroomActor(): ClassroomActor | undefined {
  const {currentAccount} = useSession()
  const {data: profile} = useProfileQuery({did: currentAccount?.did})
  return useMemo(() => {
    if (!currentAccount) return undefined
    /*
     * The level is the profile's: the server enriches `getProfile` for the
     * requester, and a level change goes through `putLearnerLevel`, which the
     * next profile read hands back.
     */
    const level = readLearnerLevel(profile)
    return {
      did: currentAccount.did,
      handle: profile?.handle ?? currentAccount.handle,
      displayName: profile?.displayName || undefined,
      avatar: profile?.avatar,
      accountType: readAccountType(profile),
      learningStage: level.learningStage,
      gradeLevel: level.gradeLevel,
      country: readLearnerCountry(profile),
    }
  }, [currentAccount, profile])
}

export interface CreatePermission {
  allowed: boolean
  /** Why not, in the words the UI shows. */
  reason: string | null
}

/**
 * Whether this account may create a classroom.
 *
 * Every creator-side account does: a `creator` and an `organisation` (a school)
 * both run classrooms. A learner and a parent do not create one — they join —
 * which is the case the rule exists for.
 *
 * An unknown type is allowed through: the backend does not hand the type back
 * yet, and refusing on *unknown* would hide the only way in from every creator
 * account that currently exists.
 */
export function canCreateClassroom(accountType: AccountType | null): boolean {
  return (
    accountType === null ||
    accountType === 'creator' ||
    accountType === 'organisation'
  )
}

export function useCreatePermission(): CreatePermission {
  const {currentAccount} = useSession()
  const {data: profile, isPending} = useProfileQuery({
    did: currentAccount?.did,
  })
  const accountType = useMemo(() => readAccountType(profile), [profile])

  /*
   * **No account, and no answer yet, are both a quiet no.**
   *
   * The rule below lets the *unknown* type through, because a deployment that
   * does not hand the type back must not lock its creators out — but *unknown*
   * and *not fetched yet* are different facts, and the button used to be drawn
   * on the strength of the second: a learner opening their own Classrooms tab
   * saw **Create** for as long as their profile took to arrive, which is how a
   * student was offered a school's work. Nothing is shown and nothing is
   * explained while the answer is still on its way, and the fully unknown,
   * settled case keeps the old behaviour.
   */
  if (!currentAccount?.did || (profile === undefined && isPending)) {
    return {allowed: false, reason: null}
  }

  if (canCreateClassroom(accountType)) {
    return {allowed: true, reason: null}
  }
  return {
    allowed: false,
    reason:
      accountType === 'learner'
        ? 'A learner account cannot create a classroom. Join one with the code your school gave you.'
        : 'Only creator accounts can create a classroom.',
  }
}
